import { useCallback, useReducer } from 'react'

import {
  Box,
  CircularProgress,
  darken,
  TextField,
  Typography,
} from '@mui/material'
import { IconChevronLeft } from '@tabler/icons-react'

import { useMutation, useQuery } from '@redwoodjs/web'

import Row from 'src/components/Row/Row'
import { CREATE_THREAD } from 'src/components/Threads/mutations'
import { GET_THREADS_FOR_OBJECT_QUERY } from 'src/components/Threads/queries'
import ThreadProvider from 'src/components/Threads/V2/Provider/ThreadProvider'
import ThreadMessages from 'src/components/Threads/V2/ThreadMessages'
import { editorStyle } from 'src/components/Tiptap/styles'
import { logger } from 'src/lib/logger'
import { shadows } from 'src/lib/style'

import SidebarHeader from './SidebarHeader'
import SidebarTabs from './SidebarTabs'
import useSidebar from './useSidebar'

const containerSx = {
  height: '100%',
  overflow: 'auto',

  '& .sidebar-thread': {
    height: 'calc(100% - 120px)',
    overflow: 'hidden',
    position: 'relative',
  },
  '& .sidebar-thread-footer': {
    height: '96px',
    width: '686px',
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    right: 16,

    '& .MuiTypography-root': {
      fontSize: '11px',
      fontWeight: 500,
      letterSpacing: '-0.22px',
      cursor: 'pointer',
      opacity: 0.8,
    },

    '& .MuiTextField-root': {
      mt: 1,
      height: '100%',
      backgroundColor: (theme) => theme.palette.background.paper,
    },
  },
  '& .sidebar-content-mode-threads-inner': {
    padding: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    justifyContent: 'space-between',
    height: 'calc(100% - 120px)',
    overflow: 'auto',

    '& .sidebar-previous-threads': {
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      overflow: 'auto',

      '& .MuiTypography-root': {
        fontSize: '12px',
        fontWeight: 500,
        letterSpacing: '-0.22px',
        cursor: 'pointer',
      },
    },

    '& .sidebar-content-mode-threads-inner-suggested-topics-list': {
      display: 'flex',
      flexDirection: 'row',
      gap: 1,
      mb: 2,

      '& .sidebar-suggested-topic': {
        padding: 1,
        backgroundColor: (theme) => theme.palette.background.paper,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        cursor: 'pointer',
        fontWeight: 500,
        letterSpacing: '-0.22px',
        fontSize: '12px',
        '&:hover': {
          backgroundColor: (theme) => theme.palette.background.paper,
        },
        borderRadius: '6px',
      },
    },
  },
  '& .sidebar-thread-container': {
    backgroundColor: (theme) => theme.palette.background.default,
    height: '100%',
    width: '100%',
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    overflowX: 'hidden',
    overflowY: 'auto',

    '& .thread-create-loading-container': {
      height: '100%',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },

    '& .thread-create-loading': {
      height: '64px',
      position: 'relative',
      gap: 1,
      '& .MuiTypography-root': {
        fontWeight: 500,
        letterSpacing: '-0.32px',
        opacity: 0.5,
        lineHeight: '160%',
        fontSize: '13px',
      },
    },
    '& .sidebar-thread-container-inner': {
      height: '100%',
      position: 'relative',
    },
    '& .thread-messages-container': {
      height: '100%',
      flexDirection: 'column',
      gap: 2,
      width: 'calc(728px + 56px)',
      maxWidth: 'calc(728px + 56px)',

      '& .thread-messages': {
        height: '100%',
        pt: '48px',
        px: 6,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        scrollMarginBottom: '128px',
        '& .thread-messages-loading': {
          mt: 2,
          height: '64px',
          position: 'relative',
          gap: 1,
          color: (theme) => theme.palette.text.secondary,
          '& .MuiTypography-root': {
            fontWeight: 500,
            letterSpacing: '-0.32px',
            opacity: 0.7,
            lineHeight: '160%',
            fontSize: '13px',
            color: (theme) => theme.palette.text.secondary,
          },
        },

        '& .thread-messages-end-divider': { height: '128px', flexShrink: 0 },
        '& .thread-messages-end-divider.ai-drafting': {
          height: 'calc(100vh - 168px)',
          flexShrink: 0,
        },
      },
      '& .thread-message': {
        width: '100%',
        mt: 2,
        flexShrink: 0,

        height: 'auto',
        minHeight: '12px',
        background: (theme) => theme.palette.background.paper,
        borderRadius: '6px',
        border: 'none',
        overflow: 'hidden',
        py: 1,
        '&.initialized': {
          boxShadow: (theme) => shadows[theme.palette?.mode]?.md,
        },
        transition: 'box-shadow 1.0s ease-in-out',

        '&:last-of-type': {
          mb: '128px',
        },

        '&.thread-message-user': {
          background: (theme) =>
            `linear-gradient(to bottom, ${darken(theme.palette.background.default, 0.03)}, ${darken(theme.palette.background.default, 0.07)})`,
          alignItems: 'flex-start',
          gap: 1,
          pl: 2,
          '&.initialized': {
            boxShadow: (theme) => shadows[theme.palette?.mode]?.md,
          },
          '& .thread-message-avatar': {
            height: '24px',
            borderRadius: '100px',
            '& .MuiAvatar-root': {
              height: '24px',
              width: '24px',
            },
            boxShadow: (theme) => shadows[theme.palette?.mode]?.md,
          },
          '& .MuiTypography-root': {
            mt: '2px',
            fontWeight: 600,
            letterSpacing: '-0.32px',
            opacity: 0.8,
            lineHeight: '160%',
            fontSize: '12px',
          },
        },
        '& .thread-message-tiptap': {
          background: (theme) => theme.palette.background.paper,
          width: '100%',
          pt: '12px',
          px: 3,
          ...editorStyle,
          pb: '0px !important',
          '& .tiptap.ProseMirror': {
            pb: '0px !important',
            '& table': {
              background: (theme) => theme.palette.background.paper,
            },
          },
        },
      },
    },
    '& .thread-messages-loading': {
      height: '100%',
      position: 'relative',
    },
  },
}

const dummyTopics = [
  'What did we last talk about?',
  'Who is the CEO of the company?',
  'What is the company culture?',
  'What is the company mission?',
]

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_THREAD':
      return action.payload
    case 'RESET_THREAD':
      return null
  }
}

const SidebarModeThreads = () => {
  const { object } = useSidebar()

  const [state, dispatch] = useReducer(reducer, null)

  const { data: threadsForObjectData } = useQuery(
    GET_THREADS_FOR_OBJECT_QUERY,
    {
      variables: {
        objectType: object.objectType,
        objectId: object.objectId,
        workspaceId: object.workspaceId,
      },
    }
  )

  const [createThread, { loading: createThreadLoading }] =
    useMutation(CREATE_THREAD)

  const handleCreateThread = useCallback(
    async (prompt: string) => {
      const { data } = await createThread({
        variables: {
          workspaceId: object.workspaceId,
          userPrompt: prompt,
          sourceObject: {
            objectId: object.objectId,
            objectType: object.objectType,
            workspaceId: object.workspaceId,
          },
          dismissedObjects: [],
          contextObjects: [],
        },
      })

      dispatch({ type: 'SET_THREAD', payload: data.createThread })
    },
    [createThread, object.workspaceId, object.objectId, object.objectType]
  )

  const handleSelectThread = useCallback(
    (threadId: string) => {
      const thread = threadsForObjectData?.threadsForObjectForUser.find(
        (thread) => thread.id === threadId
      )
      dispatch({ type: 'SET_THREAD', payload: thread })
    },
    [threadsForObjectData]
  )

  logger.dev({ state, threadsForObjectData })

  const threads = threadsForObjectData?.threadsForObjectForUser || []

  return (
    object && (
      <Box
        className="sidebar-content-mode-view"
        sx={containerSx}
      >
        <SidebarHeader />
        <SidebarTabs />
        <Box className="sidebar-thread">
          {createThreadLoading ? (
            <Row className="thread-creation-loading">
              <CircularProgress />
              <Typography>Thinking...</Typography>
            </Row>
          ) : (
            <>
              {!state?.id ? (
                <Box className="sidebar-content-mode-threads-inner">
                  <Box className="sidebar-content-mode-threads-inner-suggested-topics">
                    <Typography variant="h6">Previous Threads</Typography>
                    <Box className="sidebar-previous-threads">
                      {threads.map((thread) => (
                        <Typography
                          key={thread.id}
                          onClick={() => handleSelectThread(thread.id)}
                        >
                          {thread.title}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                  <Box>
                    <Box className="sidebar-content-mode-threads-inner-suggested-topics-list">
                      {dummyTopics.map((topic, index) => (
                        <Typography
                          className="sidebar-suggested-topic"
                          key={`topic-${index}`}
                          onClick={() => handleCreateThread(topic)}
                        >
                          {topic}
                        </Typography>
                      ))}
                    </Box>
                    <TextField
                      multiline
                      rows={4}
                      fullWidth
                      variant="outlined"
                      placeholder="Ask Maria anything..."
                    />
                  </Box>
                </Box>
              ) : (
                <ThreadProvider
                  workspaceId={object.workspaceId}
                  threadId={state.id}
                >
                  <Box className="sidebar-thread-container">
                    <Box className="sidebar-thread-container-inner">
                      <ThreadMessages />
                    </Box>

                    <Box className="sidebar-thread-footer">
                      <Row
                        gap={'4px'}
                        onClick={() => dispatch({ type: 'RESET_THREAD' })}
                      >
                        <IconChevronLeft
                          size={14}
                          stroke={2}
                        />
                        <Typography>Back to all threads</Typography>
                      </Row>
                      <TextField
                        multiline
                        rows={2}
                        fullWidth
                        variant="outlined"
                        placeholder="Ask Maria anything..."
                      />
                    </Box>
                  </Box>
                </ThreadProvider>
              )}
            </>
          )}
        </Box>
      </Box>
    )
  )
}

export default SidebarModeThreads
